<template>
  <div
    class="flex flex-col lg:flex-row items-stretch gap-3"
    data-testid="impact-chain-strip"
  >
    <div
      class="flex-1 rounded-[12px] p-4 flex flex-col min-w-0 border border-[#F0F0F0]"
      :style="{ backgroundColor: behaviorBg }"
      data-testid="impact-chain-behavior"
    >
      <p class="text-[10px] uppercase tracking-wider font-medium mb-2 m-0" :style="{ color: behaviorLabel }">
        {{ $t('UserProjects.impactAnalysis.stages.behavior') }}
      </p>
      <template v-if="patternDetected">
        <p class="text-[12px] text-[var(--color-disabled)] m-0">
          {{ $t('UserProjects.impactAnalysis.chain.tbiThisWeek') }}
        </p>
        <p class="text-[18px] font-bold text-[var(--color-dark)] m-0 mt-1">
          {{ behavior?.tbi ?? '—' }}
          <span
            v-if="behaviorDeltaLabel"
            class="text-[13px] font-medium ml-1"
            :style="{ color: behaviorDeltaColor }"
          >
            ({{ behaviorDeltaLabel }})
          </span>
        </p>
      </template>
      <template v-else>
        <p class="text-[14px] font-medium text-[var(--color-dark)] m-0">
          {{ $t('UserProjects.impactAnalysis.chain.noBehaviorPattern') }}
        </p>
        <p class="text-[13px] text-[var(--color-dark)] m-0 mt-1">
          {{ $t('UserProjects.impactAnalysis.chain.tbiThisWeek') }}: {{ behavior?.tbi ?? '—' }}
        </p>
      </template>
      <div class="mt-2 pt-2 border-t border-[#F0F0F0]">
        <router-link
          to="/user-projects/cockpit"
          class="text-[12px] font-medium no-underline hover:underline"
          :style="{ color: behaviorLabel }"
        >
          {{ $t('UserProjects.impactAnalysis.chain.toCockpit') }}
        </router-link>
      </div>
    </div>

    <div class="hidden lg:flex items-center justify-center shrink-0 text-[#D0D0D0]" aria-hidden="true">
      <i class="pi pi-chevron-right" />
    </div>

    <div
      class="flex-1 rounded-[12px] p-4 flex flex-col min-w-0 border border-[#F0F0F0]"
      :style="{ backgroundColor: causeBg }"
      data-testid="impact-chain-cause"
    >
      <p class="text-[10px] uppercase tracking-wider font-medium mb-2 m-0" :style="{ color: causeLabel }">
        {{ $t('UserProjects.impactAnalysis.stages.cause') }}
      </p>
      <template v-if="patternDetected && cause?.dominantLabel">
        <p class="text-[12px] text-[var(--color-disabled)] m-0">
          {{ $t('UserProjects.impactAnalysis.chain.topCategory') }}
        </p>
        <p class="text-[15px] font-semibold text-[var(--color-dark)] m-0 mt-1">
          "{{ cause.dominantLabel }}"
        </p>
        <p class="text-[13px] text-[var(--color-disabled)] m-0 mt-1">
          {{ cause.count }}× · {{ cause.percent }}%
        </p>
        <p v-if="cause.openConstraints > 0" class="text-[12px] m-0 mt-1" style="color: #EAB308">
          ↳ {{ $t('UserProjects.impactAnalysis.chain.openConstraints', { count: cause.openConstraints }) }}
        </p>
      </template>
      <template v-else>
        <p class="text-[14px] font-medium text-[var(--color-dark)] m-0">
          {{ $t('UserProjects.impactAnalysis.chain.noDominantCause') }}
        </p>
      </template>
      <div class="mt-2 pt-2 border-t border-[#F0F0F0]">
        <router-link
          to="/user-projects/cockpit"
          class="text-[12px] font-medium no-underline hover:underline"
          :style="{ color: causeLabel }"
        >
          {{ $t('UserProjects.impactAnalysis.chain.toCockpit') }}
        </router-link>
      </div>
    </div>

    <div class="hidden lg:flex items-center justify-center shrink-0 text-[#D0D0D0]" aria-hidden="true">
      <i class="pi pi-chevron-right" />
    </div>

    <div
      class="flex-1 rounded-[12px] p-4 flex flex-col min-w-0 border border-[#F0F0F0] bg-white"
      data-testid="impact-chain-kpi"
    >
      <p class="text-[10px] uppercase tracking-wider font-medium mb-2 m-0 text-[#ABABAB]">
        {{ $t('UserProjects.impactAnalysis.stages.kpi') }}
      </p>
      <p class="text-[12px] text-[var(--color-disabled)] m-0">
        {{ $t('UserProjects.impactAnalysis.chain.ppcThisWeek') }}
      </p>
      <p class="text-[18px] font-bold m-0 mt-1" :style="{ color: ppcColor }">
        {{ kpi?.ppc != null ? `${kpi.ppc}%` : '—' }}
      </p>
      <p class="text-[13px] text-[var(--color-disabled)] m-0 mt-1">
        {{ $t('UserProjects.impactAnalysis.chain.prevWeek') }}
        {{ kpi?.ppcPrev != null ? `${kpi.ppcPrev}%` : '—' }}
        <span v-if="ppcDeltaLabel" :style="{ color: ppcDeltaColor }"> · Δ {{ ppcDeltaLabel }}</span>
      </p>
      <p class="text-[12px] text-[var(--color-disabled)] m-0">
        {{ $t('UserProjects.impactAnalysis.chain.targetGap') }}:
        {{ kpi?.targetGap != null ? `${kpi.targetGap > 0 ? '+' : ''}${kpi.targetGap}%` : '—' }}
      </p>
      <div class="mt-2 pt-2 border-t border-[#F0F0F0]">
        <router-link
          to="/user-projects/cockpit"
          class="text-[12px] font-medium no-underline hover:underline text-[#ABABAB]"
        >
          {{ $t('UserProjects.impactAnalysis.chain.toCockpit') }}
        </router-link>
      </div>
    </div>

    <div class="hidden lg:flex items-center justify-center shrink-0 text-[#D0D0D0]" aria-hidden="true">
      <i class="pi pi-chevron-right" />
    </div>

    <div
      class="flex-1 rounded-[12px] p-4 flex flex-col min-w-0 border border-[#F0F0F0]"
      :style="{ backgroundColor: impulsBg }"
      data-testid="impact-chain-impuls"
    >
      <p
        class="text-[10px] uppercase tracking-wider font-medium mb-2 m-0"
        :style="{ color: impulsLabel }"
      >
        {{ $t('UserProjects.impactAnalysis.stages.impuls') }}
      </p>
      <p v-if="!seesImpuls" class="text-[14px] text-[var(--color-disabled)] italic m-0">
        {{ $t('UserProjects.impactAnalysis.chain.impulsRestricted') }}
      </p>
      <template v-else-if="patternDetected && impulsPreview">
        <p class="text-[13px] text-[var(--color-dark)] leading-relaxed m-0">
          💡 {{ impulsPreview }}
        </p>
      </template>
      <p v-else class="text-[14px] text-[var(--color-disabled)] italic m-0">
        {{ $t('UserProjects.impactAnalysis.chain.noImpuls') }}
      </p>
    </div>
  </div>
</template>

<script>
import { formatSignedDelta, resolveImpactAmpel } from '@/utils/impactAnalysisDisplay';

export default {
  name: 'ImpactChainStrip',
  props: {
    patternDetected: { type: Boolean, default: false },
    seesImpuls: { type: Boolean, default: false },
    behavior: { type: Object, default: null },
    cause: { type: Object, default: null },
    kpi: { type: Object, default: null },
    impulsPreview: { type: String, default: null },
  },
  computed: {
    behaviorBg() {
      return this.patternDetected ? '#EFF6FF' : '#FFFFFF';
    },
    behaviorLabel() {
      return this.patternDetected ? '#3B82F6' : '#ABABAB';
    },
    causeBg() {
      return this.patternDetected ? '#FEFCE8' : '#FFFFFF';
    },
    causeLabel() {
      return this.patternDetected ? '#EAB308' : '#ABABAB';
    },
    impulsBg() {
      return this.patternDetected && this.seesImpuls ? '#FDF0E6' : '#FFFFFF';
    },
    impulsLabel() {
      return this.patternDetected && this.seesImpuls ? 'var(--color-orange)' : '#ABABAB';
    },
    behaviorDeltaLabel() {
      return formatSignedDelta(this.behavior?.tbiDelta);
    },
    behaviorDeltaColor() {
      const delta = this.behavior?.tbiDelta;
      if (typeof delta !== 'number') return '#6B6B6B';
      return delta < 0 ? '#EF4444' : '#22C55E';
    },
    ppcColor() {
      return resolveImpactAmpel(this.kpi?.ppc, {
        greenMin: this.kpi?.targetPercent ?? 80,
        yellowMin: 60,
      }) || 'var(--color-dark)';
    },
    ppcDeltaLabel() {
      return formatSignedDelta(this.kpi?.ppcDelta, '%');
    },
    ppcDeltaColor() {
      const delta = this.kpi?.ppcDelta;
      if (typeof delta !== 'number') return '#6B6B6B';
      return delta < 0 ? '#EF4444' : '#22C55E';
    },
  },
};
</script>
